import { useFieldErrors } from '../hooks/useFieldErrors';
import { useState } from 'react';
import { useResetOnOpen } from '../hooks/useResetOnOpen';
import { useNavigate, useParams } from 'react-router';
import {
  Text, Badge, Button, Spinner, Input, Field, Select,
  makeStyles, tokens,
  Checkbox, Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
} from '@fluentui/react-components';
import { Search24Regular, AddRegular, EditRegular, DeleteRegular, MapRegular } from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchLotes, createLote, updateLote, deleteLote } from '../api/lotes.api';
import { fetchRanchos } from '../api/rancho.api';
import { WeatherWidget } from '../components/shared/WeatherWidget';
import { fetchLoteWeather } from '../api/lotes.api';
import { onlyCampoLotes } from '../utils/lote-uso';
import { useAuthStore } from '../store/auth.store';
import { useRanchoStore } from '../store/rancho.store';
import { useT } from '../i18n';
import { NumberInput } from '../components/shared/NumberInput';
import { usePageTitle } from '../hooks/usePageTitle';
import { ColorPickerField } from '../components/shared/ColorPickerField';
import { MapPointPickerDialog } from '../components/shared/MapPointPickerDialog';
import { SearchableSelect } from '../components/shared/SearchableSelect';
import type { Lote } from '../types/api.types';
import { PageTitle } from '../components/shared/PageTitle';

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: '24px' },
  toolbar: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: '16px',
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: '16px 20px',
    cursor: 'pointer',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    ':hover': { border: `1px solid ${tokens.colorBrandStroke1}` },
  },
  cardTop:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  meta:     { fontSize: '12px', color: tokens.colorNeutralForeground3 },
  cardActions: {
    display: 'flex',
    gap: '4px',
    marginTop: '4px',
    paddingTop: '8px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  formGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    minWidth: '340px',
    paddingTop: '8px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: '12px',
  },
});

// ─── LoteFormDialog ───────────────────────────────────────────────────────────

interface LoteFormProps {
  open:     boolean;
  onClose:  () => void;
  initial?: Lote | null;
  ranchoId?: string;
  // F0 — self-register-onboarding Task 16: permite al caller reaccionar a la
  // creación (no a la edición) de un lote, ej. para encadenar el dibujo de
  // geocerca inmediatamente sin que el usuario tenga que ir manualmente al Mapa.
  onCreated?: (lote: Lote, goToMap: boolean) => void;
}

type FormState = {
  name:      string;
  cropType:  string;
  hectares:  string;
  latitude:  string;
  longitude: string;
  ranchoId:  string;
  displayColor: string | null;
  uso: 'CAMPO' | 'ADMINISTRATIVO';
};

const EMPTY_FORM: FormState = {
  name: '', cropType: '', hectares: '', latitude: '', longitude: '', ranchoId: '', displayColor: null, uso: 'CAMPO',
};

function toForm(lote: Lote): FormState {
  return {
    name:      lote.name,
    cropType:  lote.cultivo ?? '',
    hectares:  lote.hectareas?.toString() ?? '',
    latitude:  lote.latitude?.toString() ?? '',
    longitude: lote.longitude?.toString() ?? '',
    ranchoId:  lote.ranchoId,
    displayColor: lote.displayColor ?? null,
    uso: lote.uso ?? 'CAMPO',
  };
}

export function LoteFormDialog({ open, onClose, initial, ranchoId, onCreated }: LoteFormProps) {
  const s  = useStyles();
  const t  = useT();
  const qc = useQueryClient();
  const isEdit = !!initial;

  const { data: ranchos = [] } = useQuery({ queryKey: ['ranchos'], queryFn: () => fetchRanchos() });
  const ranchoOptions = ranchos.map(r => ({ id: r.ranchoId, label: r.name }));

  const [form,  setForm]  = useState<FormState>(initial ? toForm(initial) : { ...EMPTY_FORM, ranchoId: ranchoId ?? '' });
  const [error, setError] = useState('');
  const fe = useFieldErrors();
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  // Preferencia por usuario/navegador: tras crear el lote, ¿ir al mapa a dibujar la geocerca?
  const [goToMap, setGoToMap] = useState<boolean>(() => {
    try { return localStorage.getItem('agrovid.lote.goToMapAfterCreate') !== '0'; } catch { return true; }
  });
  const toggleGoToMap = (v: boolean) => {
    setGoToMap(v);
    try { localStorage.setItem('agrovid.lote.goToMapAfterCreate', v ? '1' : '0'); } catch { /* sin storage */ }
  };

  useResetOnOpen(open, () => {
    setForm(initial ? toForm(initial) : { ...EMPTY_FORM, ranchoId: ranchoId ?? '' });
    setError(''); fe.clear();
    setMapPickerOpen(false);
  });

  const num = (v: string) => { const n = parseFloat(v); return v && !isNaN(n) ? n : undefined; };
  // El mapa abre en el punto del lote; si no hay, centrado en el rancho activo/elegido.
  const pickerRancho = ranchos.find(r => r.ranchoId === (form.ranchoId || ranchoId));

  const set = (patch: Partial<FormState>) => {
    setForm(f => ({ ...f, ...patch }));
    Object.keys(patch).forEach(k => fe.clear(k));
  };

  const createMut = useMutation({
    mutationFn: () => createLote({
      ranchoId:  form.ranchoId.trim(),
      name:      form.name.trim(),
      cropType:  form.cropType.trim() || undefined,
      hectares:  form.hectares ? parseFloat(form.hectares) : undefined,
      latitude:  form.latitude  ? parseFloat(form.latitude)  : undefined,
      longitude: form.longitude ? parseFloat(form.longitude) : undefined,
      displayColor: form.displayColor ?? undefined,
      uso: form.uso,
    }),
    onSuccess: (created) => { qc.invalidateQueries({ queryKey: ['lotes'] }); onClose(); onCreated?.(created, goToMap); },
    onError: (e: any) => setError(e?.response?.data?.error ?? t.common.errorUnexpected),
  });

  const updateMut = useMutation({
    mutationFn: () => updateLote(initial!.loteId, {
      name:      form.name.trim(),
      cropType:  form.cropType.trim() || undefined,
      hectares:  form.hectares  ? parseFloat(form.hectares)  : undefined,
      latitude:  form.latitude  ? parseFloat(form.latitude)  : undefined,
      longitude: form.longitude ? parseFloat(form.longitude) : undefined,
      displayColor: form.displayColor,
      uso: form.uso,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['lotes'] }); onClose(); },
    onError: (e: any) => setError(e?.response?.data?.error ?? t.common.errorUnexpected),
  });

  const saving = createMut.isPending || updateMut.isPending;

  const handleSubmit = () => {
    setError('');
    const ok = fe.validate({
      name: !form.name.trim() && t.common.validation.nameRequired,
      ranchoId: !isEdit && !form.ranchoId.trim() && t.common.validation.ranchoRequired,
    });
    if (!ok) return;
    isEdit ? updateMut.mutate() : createMut.mutate();
  };

  return (
    <>
    <Dialog open={open && !mapPickerOpen} onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
      <DialogSurface style={{ maxWidth: '460px' }}>
        <DialogBody>
          <DialogTitle>{isEdit ? t.field.editTitle.replace('{name}', initial?.name ?? '') : t.field.newLoteTitle}</DialogTitle>
          <DialogContent>
            <div className={s.formGrid}>
              <Field label={t.field.lotNameLabel} {...fe.props('name')}>
                <Input value={form.name} onChange={(_, d) => set({ name: d.value })} placeholder="Lote 3" disabled={saving} />
              </Field>
              {/* Con un rancho ya elegido en la barra superior no se vuelve a preguntar:
                  el lote se crea ahí. Solo con "Todos los ranchos" se muestra el selector. */}
              {!isEdit && ranchoId && (
                <Field label={t.field.ranchoIdLabel}>
                  <Input value={ranchoOptions.find(o => o.id === ranchoId)?.label ?? ''} readOnly disabled />
                </Field>
              )}
              {!isEdit && !ranchoId && (
                <SearchableSelect
                  label={t.field.ranchoIdLabel}
                  placeholder={t.field.searchRanchoPlaceholder}
                  noResultsText={t.common.searchNoResults}
                  options={ranchoOptions}
                  value={form.ranchoId}
                  onChange={(id) => set({ ranchoId: id })}
                  error={fe.errors.ranchoId}
                  disabled={saving}
                />
              )}
              <Field label={t.field.cropTypeLabel}>
                <Input value={form.cropType} onChange={(_, d) => set({ cropType: d.value })} placeholder="Ejote, Bitter melon..." disabled={saving} />
              </Field>
              <Field label={t.field.hectaresLabel}>
                <NumberInput value={form.hectares} onValueChange={(v) => set({ hectares: v })} placeholder="20.47" disabled={saving} />
              </Field>
              <div className={s.formRow}>
                <Field label={t.field.latitudeLabel}>
                  <Input type="number" value={form.latitude} onChange={(_, d) => set({ latitude: d.value })} placeholder="22.3456" disabled={saving} />
                </Field>
                <Field label={t.field.longitudeLabel}>
                  <Input type="number" value={form.longitude} onChange={(_, d) => set({ longitude: d.value })} placeholder="-98.7890" disabled={saving} />
                </Field>
              </div>
              <Field label={t.field.usoLabel}>
                <Select
                  aria-label={t.field.usoLabel}
                  value={form.uso}
                  onChange={(_, d) => set({ uso: d.value === 'ADMINISTRATIVO' ? 'ADMINISTRATIVO' : 'CAMPO' })}
                  disabled={saving}
                >
                  <option value="CAMPO">{t.field.usoCampo}</option>
                  <option value="ADMINISTRATIVO">{t.field.usoAdministrativo}</option>
                </Select>
              </Field>
              <Field label={t.common.colorLabel}>
                <ColorPickerField value={form.displayColor} onChange={v => set({ displayColor: v })} disabled={saving} />
              </Field>
              <Button appearance="secondary" size="small" onClick={() => setMapPickerOpen(true)} disabled={saving}>
                {t.field.pickOnMapBtn}
              </Button>
              {!isEdit && (
                <Checkbox
                  checked={goToMap}
                  onChange={(_, d) => toggleGoToMap(!!d.checked)}
                  disabled={saving}
                  label={t.field.goToMapAfterCreate}
                />
              )}
              {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={saving}
              icon={saving ? <Spinner size="tiny" /> : undefined}
            >
              {isEdit ? t.field.saveChanges : t.field.createLote}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
    {/* Hermano del Dialog del formulario (PR #305). Además el formulario se oculta mientras el
        picker está abierto: dos modales a la vez dejaban el teclado muerto al volver. */}
    <MapPointPickerDialog
      open={mapPickerOpen}
      onClose={() => setMapPickerOpen(false)}
      initialLat={num(form.latitude)}
      initialLon={num(form.longitude)}
      fallbackLat={pickerRancho?.latitude}
      fallbackLon={pickerRancho?.longitude}
      title={(form.name.trim() || initial?.name)
        ? t.field.locationDialogTitle.replace('{name}', form.name.trim() || initial?.name || '')
        : t.field.locationDialogTitleGeneric}
      onConfirm={(lat, lon) => set({ latitude: lat.toString(), longitude: lon.toString() })}
    />
    </>
  );
}

// ─── DeleteLoteDialog ─────────────────────────────────────────────────────────

export function DeleteLoteDialog({ lote, onClose }: { lote: Lote | null; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const [error, setError] = useState('');

  const mut = useMutation({
    mutationFn: () => deleteLote(lote!.loteId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['lotes'] }); setError(''); onClose(); },
    onError: (e: any) => setError(e?.response?.data?.error ?? t.common.errorUnexpected),
  });

  return (
    <Dialog open={!!lote} onOpenChange={(_, d) => { if (!d.open) { setError(''); onClose(); } }} modalType="alert">
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t.field.deleteTitle}</DialogTitle>
          <DialogContent>
            <Text>
              {t.field.deleteMessagePrefix}<strong>{lote?.name ?? ''}</strong>{t.field.deleteMessageSuffix}
            </Text>
            {error && (
              <Text style={{ display: 'block', marginTop: '8px', color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>
                {error}
              </Text>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={mut.isPending}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              style={{ backgroundColor: tokens.colorStatusDangerBackground3 }}
              onClick={() => mut.mutate()}
              disabled={mut.isPending}
              icon={mut.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {t.common.delete}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ─── FieldPage ────────────────────────────────────────────────────────────────

export function FieldPage() {
  const s       = useStyles();
  const t       = useT();
  usePageTitle(t.pageTitle.field);
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const { user } = useAuthStore();
  const isAdmin  = user?.roles.some(r => r === 'ADMIN' || r === 'SUPER_ADMIN') ?? false;

  const [search,      setSearch]     = useState('');
  const [formOpen,    setFormOpen]   = useState(false);
  const [editTarget,  setEditTarget] = useState<Lote | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Lote | null>(null);

  // Fase 1 rancho selector (2026-09-27) — antes se pedían SIEMPRE todos los
  // lotes del tenant sin importar el rancho activo (bug reportado: lotes de
  // distintos ranchos mezclados en una sola lista). `activeRanchoId` es
  // `null` cuando el usuario no ha elegido rancho (o solo tiene uno) — en
  // ese caso `fetchLotes` se comporta exactamente igual que antes.
  const { activeRanchoId } = useRanchoStore();
  const { data: allLotes = [], isLoading } = useQuery({
    queryKey: ['lotes', activeRanchoId],
    queryFn: () => fetchLotes(activeRanchoId),
  });
  // Solo lotes de Campo: los administrativos no se muestran en esta pantalla.
  const lotes = onlyCampoLotes(allLotes);

  const { data: weather } = useQuery({
    queryKey: ['weather', (lotes as Lote[])[0]?.loteId],
    queryFn:  () => fetchLoteWeather((lotes as Lote[])[0].loteId),
    enabled:  (lotes as Lote[]).length > 0,
  });

  const filtered = (lotes as Lote[]).filter((l) =>
    l.name.toLowerCase().includes(search.toLowerCase()) ||
    l.cultivo?.toLowerCase().includes(search.toLowerCase())
  );

  const lotCount = filtered.length;
  const lotLabel = lotCount === 1 ? t.field.lots : t.field.lotsPlural;

  const openCreate = () => { setEditTarget(null); setFormOpen(true); };

  // F0 — self-register-onboarding Task 16: al crear un lote nuevo, saltamos
  // directo al Mapa con ese lote preseleccionado y ?draw=1 — MapPage detecta
  // el query param, selecciona el lote y entra a drawMode automáticamente
  // (ver MapPage.tsx). El draw mode del Mapa vive enredado con el resto de su
  // estado de página (lista de lotes/módulos cargada por query, selección
  // compartida con módulos F1, diálogo de descarte, MapView/Leaflet) — no es
  // un componente aislado reutilizable como modal sin reescribir esa lógica,
  // así que en vez de extraerlo se encadena vía navegación con el lote ya
  // elegido, evitando que el usuario tenga que ir manualmente a "Mapa".
  const handleLoteCreated = (lote: Lote, goToMap: boolean) => {
    // Casilla del formulario: con ella apagada se vuelve al listado de lotes (geocerca/módulos
    // se agregan después desde la tarjeta del lote).
    navigate(goToMap
      ? `/${tenantId}/mapa?loteId=${encodeURIComponent(lote.loteId)}&draw=1`
      : `/${tenantId}/campo`);
  };
  const openEdit   = (e: React.MouseEvent, lote: Lote) => {
    e.stopPropagation();
    setEditTarget(lote);
    setFormOpen(true);
  };

  return (
    <div className={s.root}>
      <PageTitle>{t.field.title}</PageTitle>

      {weather && <WeatherWidget weather={weather} />}

      <div className={s.toolbar}>
        <Input
          contentBefore={<Search24Regular />}
          placeholder={t.field.searchPlaceholder}
          value={search}
          onChange={(_, d) => setSearch(d.value)}
          style={{ width: '280px' }}
        />
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
          {lotCount} {lotLabel}
        </Text>
        {isAdmin && (
          <Button appearance="primary" icon={<AddRegular />} onClick={openCreate} style={{ marginLeft: 'auto' }}>
            {t.field.newLot}
          </Button>
        )}
      </div>

      {isLoading ? (
        <Spinner size="small" label={t.field.loadingLots} />
      ) : filtered.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: tokens.colorNeutralForeground3 }}>
          <p>{search ? t.field.noResultsSearch : t.field.noLotsRegistered}</p>
          {isAdmin && !search && (
            <Button appearance="primary" icon={<AddRegular />} onClick={openCreate}>
              {t.field.createFirst}
            </Button>
          )}
        </div>
      ) : (
        <div className={s.grid}>
          {filtered.map((lote) => (
            <div key={lote.loteId} className={s.card} onClick={() => navigate(`/${tenantId}/campo/${lote.loteId}`)}>
              <div className={s.cardTop}>
                <Text weight="semibold">{lote.name}</Text>
                <Badge
                  color={lote.status === 'ACTIVE' ? 'success' : lote.status === 'FALLOW' ? 'warning' : 'informative'}
                  appearance="tint"
                  size="small"
                >
                  {t.field.lotStatus[(lote.status as keyof typeof t.field.lotStatus)] ?? lote.status}
                </Badge>
              </div>
              {lote.cultivo && <Text size={200}>{lote.cultivo}</Text>}
              <div className={s.meta}>
                {lote.hectareas != null ? `${lote.hectareas} ${t.field.hectares}` : '—'}
              </div>
              <div
                data-testid={`geofence-status-${lote.loteId}`}
                data-state={lote.hasBoundary ? 'captured' : 'missing'}
                style={{ color: lote.hasBoundary ? tokens.colorPaletteGreenForeground1 : tokens.colorPaletteRedForeground1 }}
              >
                <Text size={200} weight="semibold">
                  {lote.hasBoundary ? t.field.geofenceCaptured : t.field.geofenceMissing}
                </Text>
              </div>
              {isAdmin && (
                <div className={s.cardActions}>
                  <Button
                    size="small"
                    appearance="subtle"
                    icon={<MapRegular />}
                    onClick={(e) => { e.stopPropagation(); navigate(`/${tenantId}/mapa?loteId=${encodeURIComponent(lote.loteId)}&draw=1`); }}
                  >
                    {lote.hasBoundary ? t.field.editGeofence : t.field.addGeofence}
                  </Button>
                  <Button
                    size="small"
                    appearance="subtle"
                    onClick={(e) => { e.stopPropagation(); navigate(`/${tenantId}/mapa?loteId=${encodeURIComponent(lote.loteId)}&modules=1`); }}
                  >
                    {t.field.addModules}
                  </Button>
                  <Button
                    size="small"
                    appearance="subtle"
                    icon={<EditRegular />}
                    onClick={(e) => openEdit(e, lote)}
                  >
                    {t.field.editLot}
                  </Button>
                  <Button
                    size="small"
                    appearance="subtle"
                    icon={<DeleteRegular />}
                    style={{ color: tokens.colorStatusDangerForeground1 }}
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(lote); }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Dialog — key forces remount for create vs edit */}
      <LoteFormDialog
        key={editTarget?.loteId ?? 'new-lote'}
        open={formOpen}
        onClose={() => setFormOpen(false)}
        initial={editTarget}
        ranchoId={activeRanchoId ?? undefined}
        onCreated={handleLoteCreated}
      />
      <DeleteLoteDialog lote={deleteTarget} onClose={() => setDeleteTarget(null)} />
    </div>
  );
}
